<template>
  <div :class="classList"
       role="dialog"
       aria-modal="true"
       aria-label="Image Selector"
       ref="imageSelector"
       @dragover="dragOver"
       @dragleave="dragLeave"
       @drop="dropFiles"
       tabindex="0">
    <transition name="transition-fade">
      <div v-if="isDragging"
           data-dropzone
           class="inset-0 bg-white absolute pointer-events-none z-50">
        <div class="m-5 border-8 border-dashed border-gray-300 inset-0 absolute flex items-center jusitfy-center">
          <div class="text-center w-full text-3xl font-bold text-gray-500">
            Drop your files here to upload
          </div>
        </div>
      </div>
    </transition>
    <Loader
        overlay
        :visible="loading"
        class="absolute inset-0 text-blue-500"
        :label="$t('message.constants.messages.loadingProcessingImage')"
    />
    <Dialog
        v-show="removeIndex !== null"
        data-remove-confirm
        has-cancel-option
        :visible="removeIndex !== null"
        :heading="$t('message.imageSelector.removeUpload.heading')"
        :cancel-label="$t('message.imageSelector.removeUpload.cancelLabel')"
        :confirm-label="$t('message.imageSelector.removeUpload.confirmLabel')"
        @confirm="removeUpload(removeIndex)"
        @cancel="removeIndex = null"
    >
      <p>{{ $t('message.imageSelector.removeUpload.warning') }}</p>
    </Dialog>
    <Header class="border-b">
      <p class="absolute top-50 left-50 -translate-center font-bold">
        {{ $t('message.imageSelector.selectAnImage') }}
      </p>
    </Header>

    <Body class="bg-gray-50">
    <Tabs class="border-b">
      <template v-slot:labels>
        <TabLabel
            :label="$t('message.imageSelector.tabs.upload')"
            :active="selectedTab === 0"
            :aria-selected="selectedTab === 0"
            @click="handleTabSelect(0)"
            aria-role="tab"
        />
        <TabLabel
            v-if="enableFacebook"
            :label="$t('message.imageSelector.tabs.facebook')"
            :active="selectedTab === 1"
            :aria-selected="selectedTab === 1"
            @click="handleTabSelect(1)"
            aria-role="tab"
        />
        <TabLabel
            v-if="enableInstagram"
            :label="$t('message.imageSelector.tabs.instagram')"
            :active="selectedTab === 2"
            :aria-selected="selectedTab === 2"
            @click="handleTabSelect(2)"
            aria-role="tab"
        />
      </template>
      <template v-slot:content>
        <transition-group
            :name="`transition-slide-${transition}`"
            tag="div"
            ref="tabContent"
            aria-label="tabpanel"
            tabindex="0"
        >
          <TabContent
              v-show="selectedTab === 0"
              key="a"
              data-tab-uploads
              class="flex flex-col"
          >
            <Body>
            <div :class="thumbnailWrapperClassList">
              <div
                  data-upload
                  :class="thumbnailClassList"
              >
                <div v-if="isEmptyState"
                     class="p-4">
                  <p class="font-bold text-lg mb-2 leading-tight">
                    {{ $t('message.imageSelector.noUploadedImages') }}
                  </p>
                  <p class="text-gray-500 leading-tight text-sm">
                    {{ $t('message.imageSelector.uploadPhotosDirectly') }}
                  </p>
                </div>
                <div :class="uploaderClassList">
                  <Uploader
                      :uploading="uploading"
                      :isActionBtn="isEmptyState"
                      :droppedImages="droppedImages"
                      @uploadStart="handleUploadStart"
                      @uploadEnd="handleUploadEnd"
                      @uploadError="handleUploadError"
                      ref="uploader"
                  ></Uploader>
                </div>
              </div>
              <div v-for="(image, index) in uploadedImages"
                   :key="image.id"
                   class="relative w-1/4 md:w-1/6 xl:w-1/8"
              >
                <div v-if="!visibleImages.includes(image.id)"
                     :id="'placeholder-' + image.id"
                     class="pt-full"></div>
                <div v-if="visibleImages.includes(image.id)">
                  <Thumbnail
                      tabindex="0"
                      data-module-analytics-track-click data-module-analytics-category="image_providers" data-module-analytics-action="image_selected"
                      data-module-analytics-label="upload" data-module-analytics-value="1"
                      data-module-analytics-event-name="builder_select_image" :data-module-analytics-event-parameters="imageSelectedParams('upload')"
                      :image="image.thumbPath"
                      :selected="selectedImages.indexOf(image.id) !== -1"
                      :aria-label="selectedImages.indexOf(image.id) !== -1 ? 'selected image' : 'image'"
                      @click="handleSelectImage(image)"
                      @error="removeUpload(index)"
                  />
                  <Checkmark
                      :checked="selectedImages.indexOf(image.id) !== -1"
                      class="absolute top-1/8 left-1/8 -translate-center z-20 m-1"
                  />
                  <div data-remove-upload-btn
                       data-module-analytics-track-click data-module-analytics-category="imageSelector" data-module-analytics-action="click"
                       data-module-analytics-label="Confirm remove upload" data-module-analytics-value="1"
                       class="rounded-full bg-opacity-60 absolute top-0 right-0 bg-black p-1 m-1 cursor-pointer focus-visible:outline-blue-500"
                       aria-label="Remove image"
                       tabindex="0"
                       @click="handleConfirmRemoveUpload(index)"
                       @keydown.enter="handleConfirmRemoveUpload(index)"
                  >
                    <vue-feather type="x"
                                 aria-hidden="true"
                                 class="w-4 h-4 text-white block"></vue-feather>
                  </div>
                </div>
              </div>
              <div class="text-center text-gray-600 my-8 w-full">
                <p class="font-bold">{{ $t('message.imageSelector.supportedFormats.label') }}</p>
                <p class="text-sm">{{ $t('message.imageSelector.supportedFormats.formats') }}</p>
                <p v-if="uploadLater" class="text-sm">{{ $t('message.imageSelector.supportedFormats.formatUnavailable') }} <a @click="handleUploadLater" class="underline text-blue-500 cursor-pointer">{{ $t('message.imageSelector.supportedFormats.provideLater') }}</a></p>
              </div>
            </div>
            </Body>
            <transition name="transition-slide-fade-bottom">
              <div
                  v-show="showError"
                  key="upload-error"
                  class="absolute inset-x-0 bottom-0 text-center text-sm bg-red-500 text-white rounded m-4 p-2 flex items-center shadow-lg"
              >
                <div class="w-6 h-6 bg-red-800 text-white flex justify-center items-center rounded mr-2">
                  <vue-feather type="alert-circle"
                               class="w-4 h-4 block"></vue-feather>
                </div>
                <div>{{ error }}</div>
                <div
                    class="w-6 h-6 text-white flex justify-center items-center rounded ml-auto"
                    @click="showError = false"
                >
                  <vue-feather type="x"
                               class="w-4 h-4 block"></vue-feather>
                </div>
              </div>
            </transition>
          </TabContent>
          <TabContent
              v-show="selectedTab === 1"
              key="b"
              data-tab-facebook
              class="flex flex-col"
          >
            <Body>
            <div
                v-if="facebookImages.length === 0"
                class="flex-grow flex flex-wrap items-center content-center justify-center overflow-auto"
            >
              <div class="rounded m-4  bg-white shadow-lg">
                <div class="p-4">
                  <p class="font-bold text-lg mb-2 leading-tight">
                    {{ $t('message.imageSelector.facebook.title') }}
                  </p>
                  <p class="text-gray-500 leading-tight text-sm">
                    {{ $t('message.imageSelector.facebook.description') }}
                  </p>
                </div>
                <div class="p-4 bg-gray-50 text-right">
                  <ActionButton
                      data-login-facebook
                      class="btn--primary w-full sm:w-auto"
                      :label="$t('message.imageSelector.facebook.login')"
                      @click="facebookLogin"
                  />
                </div>
              </div>
            </div>
            <div
                v-else
                class="flex flex-wrap overflow-auto"
            >
              <div
                  v-for="image in facebookImages"
                  :key="image.id"
                  class="relative w-1/4 md:w-1/6 xl:w-1/8"
              >
                <Thumbnail
                    tabindex="0"
                    :aria-label="selectedImages.indexOf(image.id) !== -1 ? 'selected image' : 'image'"
                    :image="image.thumbPath ? image.thumbPath : image.picture"
                    :selected="selectedImages.indexOf(image.id) !== -1"
                    data-module-analytics-track-click data-module-analytics-category="image_providers" data-module-analytics-action="image_selected"
                    data-module-analytics-label="facebook" data-module-analytics-value="1"
                    data-module-analytics-event-name="builder_select_image" :data-module-analytics-event-parameters="imageSelectedParams('facebook')"
                    @click="handleSelectImage((image.thumbPath ? image : image.images[0].source), (image.thumbPath ? false : true), KEY_FACEBOOK)"
                />
                <Checkmark
                    :checked="selectedImages.indexOf(image.id) !== -1"
                    class="absolute top-1/8 left-1/8 -translate-center z-20 m-1"
                />
              </div>
            </div>
            </Body>
            <transition name="transition-slide-fade-bottom">
              <div
                  v-show="showError"
                  key="fb-error"
                  class="absolute inset-x-0 bottom-0 text-center text-sm bg-red-500 text-white rounded m-4 p-2 flex items-center shadow-lg"
              >
                <div class="w-6 h-6 bg-red-800 text-white flex justify-center items-center rounded mr-2">
                  <vue-feather type="alert-circle"
                               class="w-4 h-4 block"></vue-feather>
                </div>
                <div>{{ error }}</div>
                <div
                    class="w-6 h-6 text-white flex justify-center items-center rounded ml-auto"
                    @click="showError = false"
                >
                  <vue-feather type="x"
                               class="w-4 h-4 block"></vue-feather>
                </div>
              </div>
            </transition>
            <Footer
                v-if="facebookNext"
                class="p-4 border-t flex flex-col flex-shrink-0 justify-center relative bg-white w-full md:flex-row md:justify-center"
            >
              <ActionButton
                  class="btn--primary w-full md:w-1/3 lg:w-1/4 xl:w-1/5"
                  :label="$t('message.imageSelector.loadMoreImages')"
                  @click="loadFacebookImages"
              />
            </Footer>
          </TabContent>
          <TabContent
              v-show="selectedTab === 2"
              key="c"
              data-tab-instagram
              class="flex flex-col"
          >
            <Body>
            <div
                v-if="instagramImages.length === 0"
                class="flex-grow flex flex-wrap items-center content-center justify-center overflow-auto"
            >
              <div class="rounded m-4  bg-white shadow-lg">
                <div class="p-4">
                  <p class="font-bold text-lg mb-2 leading-tight">
                    {{ $t('message.imageSelector.instagram.title') }}
                  </p>
                  <p class="text-gray-500 leading-tight text-sm">
                    {{ $t('message.imageSelector.instagram.description') }}
                  </p>
                </div>
                <div class="p-4 bg-gray-50 text-right">
                  <ActionButton
                      data-login-instagram
                      class="btn--primary w-full sm:w-auto"
                      :label="$t('message.imageSelector.instagram.login')"
                      @click="instagramLogin"
                  />
                </div>
              </div>
            </div>
            <div
                v-else
                class="flex flex-wrap overflow-auto"
            >
              <div
                  v-for="image in instagramImages"
                  :key="image.id"
                  class="relative w-1/4 md:w-1/6 xl:w-1/8"
              >
                <Thumbnail
                    tabindex="0"
                    :aria-label="selectedImages.indexOf(image.id) !== -1 ? 'selected image' : 'image'"
                    :image="image.thumbPath ? image.thumbPath : image.media_url"
                    :selected="selectedImages.indexOf(image.id) !== -1"
                    data-module-analytics-track-click data-module-analytics-category="image_providers" data-module-analytics-action="image_selected"
                    data-module-analytics-label="instagram" data-module-analytics-value="1"
                    data-module-analytics-event-name="builder_select_image" :data-module-analytics-event-parameters="imageSelectedParams('instagram')"
                    @click="handleSelectImage((image.thumbPath ? image : image.media_url), (image.thumbPath ? false : true), KEY_INSTAGRAM)"/>
                <Checkmark
                    :checked="selectedImages.indexOf(image.id) !== -1"
                    class="absolute top-1/8 left-1/8 -translate-center z-20 m-1"
                />
              </div>
            </div>
            </Body>
            <transition name="transition-slide-fade-bottom">
              <div
                  v-show="showError"
                  key="ig-error"
                  class="absolute inset-x-0 bottom-0 text-center text-sm bg-red-500 text-white rounded m-4 p-2 flex items-center shadow-lg"
              >
                <div class="w-6 h-6 bg-red-800 text-white flex justify-center items-center rounded mr-2">
                  <vue-feather type="alert-circle"
                               class="w-4 h-4 block"></vue-feather>
                </div>
                <div>{{ error }}</div>
                <div
                    class="w-6 h-6 text-white flex justify-center items-center rounded ml-auto"
                    @click="showError = false"
                >
                  <vue-feather type="x"
                               class="w-4 h-4 block"></vue-feather>
                </div>
              </div>
            </transition>
            <Footer
                v-if="instagramNext"
                class="p-4 border-t flex flex-col flex-shrink-0 justify-center relative bg-white w-full md:flex-row md:justify-center"
            >
              <ActionButton
                  class="btn--primary w-full mb-2 md:mb-0 md:mx-1 md:w-1/3 lg:w-1/4 xl:w-1/5"
                  :label="$t('message.imageSelector.loadMoreImages')"
                  @click="loadInstagramImages"
              />
            </Footer>
          </TabContent>
        </transition-group>
      </template>
    </Tabs>
    </Body>

    <Footer
        v-if="uploadLater"
        class="p-4 flex flex-col flex-shrink-0 justify-center relative bg-white w-full md:justify-end md:flex-row md:justify-center lg:rounded-lg"
    >
      <ActionButton
          data-test-id="upload-later"
          class="btn--secondary w-full sm:w-auto md:mx-1 md:w-auto md:w-1/3 lg:w-1/4 xl:w-1/5"
          :label="$t('message.imageSelector.provideImageLater')"
          @click="handleUploadLater"
      />
    </Footer>
    <IconButton
        data-close
        icon="x"
        class="ml-auto right-0 top-0 z-20"
        ref="closeBtn"
        :position="'absolute'"
        :accessibility-label="$t('message.builder.header.closeAccessibility')"
        @click="handleCancel"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { upload } from '@canvaspop/scoops'

import { LOADING_PROCESSING_IMAGE } from '@vuedir/constants/messages'

import ActionButton from '@vuedir/components/ActionButton'
import Body from '@vuedir/components/Body'
import Checkmark from '@vuedir/components/Checkmark'
import Footer from '@vuedir/components/Footer'
import Header from '@vuedir/components/Header'
import IconButton from '@vuedir/components/IconButton'
import Loader from '@vuedir/components/Loader'
import TabContent from '@vuedir/components/TabContent'
import TabLabel from '@vuedir/components/TabLabel'
import Tabs from '@vuedir/components/Tabs'
import Thumbnail from '@vuedir/components/Thumbnail'
import Uploader from '@vuedir/components/Uploader'
import Dialog from '@vuedir/components/Dialog'
import VueFeather from 'vue-feather';
import { observeApp } from '@js/modules/utilities'
import { trapTabFocus } from '@canvaspop/scoops'

/**
 * ImageSelector.vue
 */
export default {
  components: {
    ActionButton,
    Body,
    Checkmark,
    Footer,
    Header,
    IconButton,
    Loader,
    TabContent,
    TabLabel,
    Tabs,
    Thumbnail,
    Uploader,
    Dialog,
    VueFeather
  },

  props: {
    selectedImages: {
      type: Array,
      default: () => [],
    },

    uploadLater: {
      type: Boolean,
      default: false,
    },

    featureFlags: {
      type: Object,
      default: () => {},
    },

    autoSelectSingleUpload: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      selectedTab: 0,
      selected: [],

      loading: false,
      loadingMessage: null,

      uploading: false,
      uploadedImages: [],

      isLoggedInToFacebook: false,
      facebookImages: [],
      facebookToken: null,
      facebookNext: null,

      isLoggedInToInstagram: false,
      instagramImages: [],
      instagramToken: null,
      instagramUserId: null,
      instagramNext: null,

      transition: 'left',
      removeIndex: null,

      showError: false,
      error: null,

      visibleImages: [],
      triggerElement: null,

      KEY_UPLOADS: 'cp-uploaded-images',
      KEY_FACEBOOK: 'cp-facebook-images',
      KEY_INSTAGRAM: 'cp-instagram-images',

      isDragging: false,
      droppedImages:  null,
      inActiveTimeout: null
    }
  },

  emits: ['close', 'confirm', 'uploadLater'],

  computed: {
    ...mapGetters({
      facebookClientId: 'app/facebookClientId',
      instagramClientId: 'app/instagramClientId',
      instagramRedirectUri: 'app/instagramRedirectUri',
    }),
    classList () {
      return [
        'absolute inset-0 bg-white z-40 flex flex-col lg:w-4/5 lg:rounded-lg lg:m-auto lg:my-12',
      ]
    },
    thumbnailWrapperClassList () {
      return [
        this.isEmptyState ? 'flex-grow items-center content-center justify-center' : '',
        'flex flex-wrap overflow-auto',
      ]
    },

    thumbnailClassList () {
      return [
        this.isEmptyState ? 'rounded m-4  bg-white shadow-lg' : 'w-1/4 md:w-1/6 xl:w-1/8',
      ]
    },

    uploaderClassList () {
      return [
        this.isEmptyState ? 'p-4 bg-gray-50  text-right' : 'pt-full relative flex items-center justify-center bg-white',
      ]
    },

    isEmptyState () {
      return this.uploadedImages.length === 0 && !this.uploading
    },
    enableFacebook () {
      return !this.featureFlags?.disableFacebook
    },
    enableInstagram () {
      return !this.featureFlags?.disableInstagram
    },
  },
  watch: {
    selectedTab: function (newVal, oldVal) {
      this.transition = newVal > oldVal ? 'right' : 'left'

      if (newVal == 3) {
        this.checkIfWalletIsConnected()
      }
    },

    selectedImages: {
      handler: function (newVal, oldVal) {
        this.selected = [...newVal]
      },
      deep: true
    },

    uploadedImages: {
      handler: function (newImages, oldImage) {
        this.writeImages(this.KEY_UPLOADS, newImages)
      },
      deep: true
    }
  },

  created () {
    this.uploadedImages = this.filterOldImages(this.readImages(this.KEY_UPLOADS))
    this.selected = [...this.selectedImages]
  },
  async mounted () {

    await this.initializeEthers()
    this.attachIntersectionObservers()
    this.focusImageSelector()

    window.fbAsyncInit = () => {
      FB.init({
        appId: this.facebookClientId,
        cookie: true,
        xfbml: true,
        version: 'v8.0',
      })

      FB.getLoginStatus((response) => {
        this.facebookStatusCallback(response)
      })
    }

    // Load the SDK asynchronously
    (function (d, s, id) {
      var js
      var fjs = d.getElementsByTagName(s)[0]
      if (d.getElementById(id)) return
      js = d.createElement(s)
      js.id = id
      js.src = '//connect.facebook.net/en_US/sdk.js'
      fjs.parentNode.insertBefore(js, fjs)
    }(document, 'script', 'facebook-jssdk'))

    window.instagramCallback = (response) => {
      this.instagramStatusCallback(response)
    }

    document.addEventListener('keydown', this.handleKeyDown)
  },
  unmounted () {
    this.focusTriggerElement()
    // cleanup event listeners with functions
    if (window.ethereum && window.ethereum.removeListener) {
      window.ethereum.removeListener('accountsChanged', this.handleAccountsChanged)
      window.ethereum.removeListener('chainChanged', this.handleChainChanged)
    }

    document.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    handleCancel () {
      this.$emit('close')
    },

    handleTabSelect (index) {
      this.selectedTab = index
      this.$nextTick(() => {
        this.$refs.tabContent.$el.focus()
      })
    },

    handleUploadStart () {
      this.showError = false
      this.uploading = true
    },

    handleUploadEnd (images) {
      images.forEach((image) => {
          // show newly added images
          this.showImage(image.id)
      })
      this.uploadedImages = images.concat(this.uploadedImages)
      if (images.length === 1 && this.autoSelectSingleUpload) {
          this.handleSelectImage(images[0])
      }
      this.uploading = false
    },

    handleUploadError (error) {
      this.error = error.data.errorMessage
      this.showError = true
      this.uploading = false
    },
    readImages (key) {
      const images = localStorage.getItem(key) || '[]'
      return JSON.parse(images)
    },

    writeImages (key, images) {
      localStorage.setItem(key, JSON.stringify(images))
    },

    filterOldImages (images) {

      const benchmarkDate = new Date()
      benchmarkDate.setDate(benchmarkDate.getDate() - 60)

      let filteredImages = images.filter((image) => {
        const dateCreated = new Date(image.dateCreated)

        if (image.dateCreated) {
          // return images less than 60 days old
          if (dateCreated >= benchmarkDate) {

            return image
          }
        } else {

          return image
        }
      })
      filteredImages.sort((imageA, imageB) => {

        return imageA.id < imageB.id ? 1 : -1
      })

      return filteredImages
    },

    async handleSelectImage (image, shouldUpload = false, provider) {
      if (shouldUpload) {
        this.loading = true

        let data = new FormData()
        data.append('imgUrl', image)
        data.append('persistable', true)

        try {
          const response = await upload(data)

          if (provider === this.KEY_FACEBOOK) {
            this.facebookImages = this.facebookImages.map(i => {
              if (i.images && i.images[0].source === image) {
                return response.data
              } else {
                return i
              }
            })
          }

          if (provider === this.KEY_INSTAGRAM) {
            this.instagramImages = this.instagramImages.map(i => {
              if (i.media_url === image) {
                return response.data
              } else {
                return i
              }
            })
          }

          this.$emit('confirm', response.data)
        } catch (error) {
          this.error = error
          this.showError = true
        }

        this.loading = false
      } else {
        this.$emit('confirm', image)
      }
    },

    handleDelete () {
      if (this.selected.length) {
        this.uploadedImages = this.uploadedImages.filter((image, index) => this.selected.indexOf(index) === -1)
        this.selected = []
      }
    },

    handleConfirmRemoveUpload (removeIndex) {
      this.removeIndex = removeIndex
    },

    removeUpload (removeIndex) {
      this.uploadedImages = this.uploadedImages.filter((image, index) => index !== removeIndex)
      this.removeIndex = null
    },

    facebookLogin () {
      FB.login(response => {
        this.facebookStatusCallback(response)
      }, {
        scope: 'user_photos',
      })
    },

    facebookLogout () {
      FB.logout(response => {
        this.facebookStatusCallback(response)
      })
    },

    facebookStatusCallback (response) {
      if (response.authResponse && response.status === 'connected') {
        this.isLoggedInToFacebook = true
        this.facebookToken = response.authResponse.accessToken

        this.loadFacebookImages()
      } else {
        this.isLoggedInToFacebook = false
        this.facebookToken = null
        this.facebookImages = []
      }
    },

    loadFacebookImages () {
      const url = this.facebookNext || '/me/photos?type=uploaded&limit=24&fields=id,picture,images'
      FB.api(url, (response) => {
        if (response.data.length) {
          this.facebookImages = this.facebookImages.concat(response.data)
          this.facebookNext = response.paging.next
        }
      })
    },

    handleUploadLater () {
      this.$emit('uploadLater')
      this.$emit('close')
    },

    async instagramLogin () {
      return window.open(`https://api.instagram.com/oauth/authorize?client_id=${this.instagramClientId}&redirect_uri=${this.instagramRedirectUri}&scope=user_profile,user_media&response_type=code`)
    },

    /**
     * Checks if the user has metamask installed
     * @returns {Promise<boolean>}
     */
    async checkIfHasMetamask () {
      if (!ethereum) {
        this.hasMetamask = false
      }
      this.hasMetamask = true
    },

    async checkIfWalletIsConnected (event) {
      await this.checkIfHasMetamask()
      if (this.hasMetamask) {
        if (window.ethereum && window.ethereum.selectedAddress) {
          await this.walletLogin()
        }
      }
    },

    instagramLogout () {
      console.log('IG logout')
    },

    instagramStatusCallback (response) {
      if (response.access_token && response.user_id) {
        this.isLoggedInToInstagram = true
        this.instagramToken = response.access_token
        this.instagramUserId = response.user_id
        this.loadInstagramImages()
      } else {
        this.isLoggedInToInstagram = false
        this.instagramToken = null
        this.instagramUserId = null
        this.instagramImages = []
      }
    },

    async loadInstagramImages () {
      const url = this.instagramNext || `https://graph.instagram.com/me/media?fields=id,media_url&access_token=${this.instagramToken}&limit=24`
      try {
        const response = await axios.get(url)
        this.instagramImages = this.instagramImages.concat(response.data.data)
        this.instagramNext = response.data.paging.next
      } catch (error) {
        this.error = error
        this.showError = true
      }
    },

    async initializeEthers () {
      const ethersPromise = import(/* webpackChunkName: 'ethers' */ 'ethers')
      const { ethers } = await ethersPromise
      this.ethers = ethers
    },

    attachIntersectionObservers () {
      this.uploadedImages.forEach((image, index) => {

        if (index <= 19) {
          // show the first 20 images, since they'll be
          // automatically visible on any screen size
          this.showImage(image.id)
        } else {
          // Attach intersection observer to each image
          this.attachIntersectionObserver(image.id)
        }
      })
    },

    attachIntersectionObserver (imageId) {
      const elementId = 'placeholder-' + imageId
      const placeholderElement = document.querySelector('#' + elementId)
      observeApp(placeholderElement, {}, async () => {
        this.showImage(imageId)
      })
    },

    showImage (imageId) {
      if (!this.visibleImages.includes(imageId)) {
        this.visibleImages.push(imageId)
      }
    },

    imageSelectedParams (provider) {

      return JSON.stringify({
        provider: provider
      })
    },

    focusImageSelector() {
      this.triggerElement = document.activeElement
      this.$nextTick(() => {
        let selector
        if (this.uploadedImages.length > 0) {
          // focus on the image selector component on mount
          selector = this.$refs.uploader?.$refs.uploadBtn?.$el
        } else {
          selector = this.$refs.uploader?.$refs.emptyUploadBtn?.$el
        }
        if (selector) {
            selector.focus()
        }
      })
    },

    focusTriggerElement() {
      this.$nextTick(() => {
        // focus on the builder component on mount
        this.triggerElement?.focus()
      })
    },

    handleKeyDown(event) {
      trapTabFocus(this.$refs.imageSelector, this.$refs.closeBtn.$el, event)
    },

    dragOver(e) {
      e.preventDefault()
      this.isDragging = true
      clearTimeout(this.inActiveTimeout)
    },

    dragLeave(e) {
      e.preventDefault()
      // set timeout to prevent flickering on new dragover events
      this.inActiveTimeout = setTimeout(() => {
        this.isDragging = false
      }, 50)
    },

    dropFiles(e) {
      e.preventDefault()
      this.droppedImages = e.dataTransfer.files
      this.isDragging = false
    },
  },
}
</script>
